import { type CommentActionsContextInterface } from '@databases/comments/context/CommentContext';
import { getBaseUrl } from '@databases/database-state';
import { type DialogContextInterface } from '@databases/dialog';
import { EditMode, FieldTypeCategory, getFieldType } from '@databases/fields';
import { type MessageFormatter } from '@databases/intl/intl-types';
import { type DefaultRootState } from '@databases/redux';
import {
  selectIsValueSelected,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
  selectSelectedValuesCount,
  selectIsDeleteColumnsDisabled,
  selectIsHoverDeleteEntriesButton,
  selectIsHoverDeleteFieldsButton,
} from '@databases/redux/cellSelections/selectors';
import { appStore } from '@databases/redux/store';
import { container } from '@databases/service-container';
import { DatabaseFieldType } from '@databases/types/database';
import { getReferencedStatusContentRefs } from '@databases/utils/database/field-types/pageStatus';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { getDatabasePath } from '@databases/utils/urls';
import { isEqual } from 'lodash-es';

import { type CellContextMenuContext } from '../types';

/**
 * This context is built on demand and passed to each menu action.
 * Right now, this encompasses all data that is needed across any of the items.
 */
export function createContextualMenuContext({
  canEditContent,
  canEditFields,
  contentId,
  currentViewId,
  entryId,
  fieldId,
  fieldTypeCategories = [
    FieldTypeCategory.BASIC,
    FieldTypeCategory.PAGE,
    FieldTypeCategory.OTHER_DATABASES,
    FieldTypeCategory.JIRA,
  ],
  formatMessage,
  isDBAiFirstCreationEnabled = false,
  dialogs,
  comments,
  panel,
}: {
  comments: {
    setOpenThread: CommentActionsContextInterface['setOpenThread'];
  };
  canEditContent: boolean;
  canEditFields: boolean;
  contentId: string;
  currentViewId: string;
  dialogs: DialogContextInterface;
  entryId: string;
  fieldId: string;
  fieldTypeCategories?: FieldTypeCategory[];
  formatMessage: MessageFormatter;
  isDBAiFirstCreationEnabled?: boolean;
  panel?: boolean;
}): CellContextMenuContext {
  const state: DefaultRootState = appStore.getState();

  const database = selectDatabaseByContentIdRequired(state, { contentId });
  const entry = getDatabaseEntryRequired(database, entryId);
  const field = getDatabaseFieldRequired(database, fieldId);
  const value = getDatabaseValue(database, field, entry);

  const isSelected = selectIsValueSelected(state, { entryId, fieldId });
  const selectedEntryIds = isSelected ? selectSelectedEntryIds(state, { contentId }) : [];
  const selectedFieldIds = isSelected ? selectSelectedFieldIds(state, { contentId }) : [];

  const selectedFieldsCount = selectedFieldIds.length;
  const selectedEntriesCount = selectedEntryIds.length;
  const selectedValuesCount = isSelected
    ? selectSelectedValuesCount(state, { contentId })
    : 0;

  const isDeletionDisabled =
    isSelected && selectIsDeleteColumnsDisabled(state, { contentId });

  const referencedContentRefsOfEntries = selectedEntryIds.flatMap((entryId) => {
    const entry = getDatabaseEntryRequired(database, entryId);

    return getReferencedStatusContentRefs({ database, field, entry }, state);
  });

  const canUsePageCategory = referencedContentRefsOfEntries.every((v) => {
    return isEqual(v.contentId, referencedContentRefsOfEntries[0].contentId);
  });

  const fieldType = getFieldType(field.type);
  const category = fieldType.category;
  const canUseCategory =
    fieldTypeCategories?.includes(FieldTypeCategory.PAGE) &&
    category === FieldTypeCategory.PAGE
      ? canUsePageCategory
      : fieldTypeCategories.includes(category);

  const canEditValue =
    fieldType.editMode(
      {
        database,
        field,
        entry,
        value,
      },
      state
    ) !== EditMode.READ_ONLY;

  const isHoverDeleteEntriesButton =
    isSelected && selectIsHoverDeleteEntriesButton(state);
  const isHoverDeleteFieldsButton = isSelected && selectIsHoverDeleteFieldsButton(state);
  const canAskRovo =
    isDBAiFirstCreationEnabled && canEditContent && selectedValuesCount > 0;
  const canInsertContent = canEditContent && selectedValuesCount > 0;
  const canClearContent =
    canInsertContent && canEditValue && field.type !== DatabaseFieldType.PAGE_STATUS;
  const canCopyValue = canEditContent && selectedValuesCount === 1;
  const canBulkEditValues =
    canEditContent &&
    selectedValuesCount > 1 &&
    selectedFieldsCount === 1 &&
    canEditValue &&
    canUseCategory;
  const canDeleteEntries = canEditContent && selectedValuesCount > 0;
  const canDeleteFields = canEditFields && selectedValuesCount > 0 && !isDeletionDisabled;
  const canOpenComments = selectedValuesCount > 0;

  return {
    baseUrl: getBaseUrl(),
    capabilities: {
      canAskRovo,
      canBulkEditValues,
      canClearContent,
      canCopyValue,
      canDeleteEntries,
      canDeleteFields,
      canInsertContent,
      canOpenComments,
    },
    comments,
    contentId,
    currentViewId,
    databasePath: getDatabasePath({
      spaceKey: database.space.key,
      contentId,
    }),
    dialogs: {
      showDialog: dialogs.showDialog,
    },
    panelManager: container.resolve('PanelManager'),
    entryId,
    fieldId,
    formatMessage,
    isHoverDeleteEntriesButton,
    isHoverDeleteFieldsButton,
    panel,
    selectedEntriesCount,
    selectedEntryIds,
    selectedFieldIds,
    selectedFieldsCount,
    selectedValuesCount,
  };
}
